import { Invitation } from './../../models/Invitation';
import { Component, OnInit, Input } from '@angular/core';
import { Project } from '@model/Project';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ProjectService } from '@service/project.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { concat, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, tap, catchError } from 'rxjs/operators';
import { Guest, UsersService } from '@service/users.service';
import { Message } from '@model/Message';

@Component({
  selector: 'app-modal-invitation',
  templateUrl: './modal-invitation.component.html',
  styleUrls: ['./modal-invitation.component.scss']
})
export class ModalInvitationComponent implements OnInit {

  @Input() public project: Project = {} as Project;
  @Input() private repositoryId: number;

  inviteFilterValue: string = 'invite';

  invitation: Invitation = {
    platform_invite: true
  } as Invitation;
  invitationForm: FormGroup;
  email: string;
  submitted: boolean = false;
  submittedUninvite: boolean = false;
  public showBlindSelection: boolean = true;

  people: any[] = [];

  guest$: Observable<Guest[]>;
  guestLoading: boolean = false;
  guestInput$ = new Subject<string>();
  selectedGuest;
  public chooseEmail: boolean = false;
  public emailAlreadyExists: boolean = false;
  public nameIsNotRequired: boolean = true;

  constructor(
    private formBuilder: FormBuilder,
    private projectService: ProjectService,
    private usersService: UsersService,
    public activeModal: NgbActiveModal,
    private toast: ToastrService
  ) { }

  ngOnInit() {
    this.invitationForm = this.formBuilder.group({
      name: [this.invitation.name],
      email: [this.invitation.email, Validators.required],
      blind_selection: [this.invitation.blind_selection],
      platform_invite: [this.invitation.platform_invite]
    });

    this.invitationForm.controls['email'].valueChanges.subscribe(() => {
      this.nameIsNotRequired = true;
      const emailField = this.invitationForm.controls['email'].value;
      if (emailField) {
        this.usersService.userAlreadyExists(emailField.email).subscribe((response) => {
          if (response) {
            this.emailAlreadyExists = response.userExists;
          }
        });
      }
    });

    const authors = this.project.authors.filter(author => !author.author_principal)
                        .map(a => ({ ...a, role: 'Autor', checked: false }));
    if (authors.length) {
      this.people.push(...authors);
    }

    const mentors = this.project.mentors.map(m => ({
      ...m,
      role: 'Mentor',
      checked: false
    }));
    if (mentors.length) {
      this.people.push(...mentors);
    }

    if (this.project.advisor) {
      const advisor = { ...this.project.advisor, role: 'Orientador', checked: false };
      this.people.push(advisor);
    }

    if (this.project.cosupervisor) {
      const cosupervisor = { ...this.project.cosupervisor, role: 'Coorientador', checked: false };
      this.people.push(cosupervisor);
    }

    this.loadGuests();
  }

  private loadGuests() {
    this.guest$ = concat(
      of([]), // default items
      this.guestInput$.pipe(
        distinctUntilChanged(),
        tap(() => this.guestLoading = true),
        switchMap(term => this.usersService.getGuests(term).pipe(
          catchError(() => of([])), // empty list on error
          tap(() => this.guestLoading = false)
        ))
      )
    );
  }

  trackByFn(item: Guest) {
    return item.email;
  }

  get role(): string {
    if (this.invitation.role === 'author') return 'Convidar autor(a)';
    if (this.invitation.role === 'advisor') return 'Convidar orientador(a)';
    if (this.invitation.role === 'co-supervisor') return 'Convidar coorientador(a)';
    if (this.invitation.role === 'mentor') return 'Convidar mentor(a)';
    else {
      this.invitation.role = 'author';
      return 'Convidar autor(a)';
    }
  }

  get f(): any { return this.invitationForm.controls; }

  selectRole(role: string) {
    this.invitation.role = role;
    this.showBlindSelection = role === 'author';
  }

  save() {
    this.submitted = true;

    if (this.invitationForm.invalid) return;

    this.submitted = false;

    this.invitation.email = this.selectedGuest.email;

    this.projectService.invitation(this.project.id, this.invitation)
      .subscribe((invitation: Invitation) => {
        this.activeModal.close(invitation);
      }, (handler) => {
        if (handler.status === 400) {
          for (const prop in handler.error) {
            if (prop !== 'success') {
              if (prop === 'name') {
                this.nameIsNotRequired = false;
              }
              this.toast.error(handler.error[prop]);
            }
          }
        }
      });
  }

  handleRadio() {
    if (this.inviteFilterValue === 'invite') {
      this.people.forEach(p => p.checked = false);
    }
  }

  onUninvite() {
    this.submittedUninvite = true;

    if (this.selectedAuthorIds.length <= 0) {
      return;
    }

    this.submittedUninvite = false;

    this.projectService.uninvite(this.project.id, this.selectedAuthorIds)
      .subscribe((project: Project) => {
        this.toast.success(Message.SUCCESS);
        this.project.advisor = project.advisor;
        this.project.cosupervisor = project.cosupervisor;
        this.project.authors = [...project.authors];
        this.project.mentors = [...project.mentors];
        this.activeModal.close('uninvite');
      }, (handler) => {
        console.log(handler);
      });
  }

  get selectedAuthorIds() {
    return this.people.filter(p => p.checked).map(p => p.id);
  }

}
